import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { styles } from '@mattilsynet/design'
import * as Mtds from '@mattilsynet/design/react'
import {
  ListBulletsIcon,
  TextBIcon,
  TextHOneIcon,
  TextHTwoIcon,
  TextHThreeIcon,
  TextItalicIcon,
  ListNumbersIcon,
  LinkSimpleIcon
} from '@phosphor-icons/react'
import {
  Editor,
  editorStateCtx,
  editorViewCtx,
  editorViewOptionsCtx,
  rootCtx,
  defaultValueCtx
} from '@milkdown/kit/core'
import {
  commonmark,
  toggleStrongCommand,
  toggleEmphasisCommand,
  headingAttr,
  wrapInHeadingCommand,
  turnIntoTextCommand,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
  toggleLinkCommand
} from '@milkdown/kit/preset/commonmark'
import { history } from '@milkdown/kit/plugin/history'
import { clipboard } from '@milkdown/kit/plugin/clipboard'
import { listener, listenerCtx } from '@milkdown/kit/plugin/listener'
import { callCommand, replaceAll } from '@milkdown/kit/utils'
import {
  Milkdown,
  MilkdownProvider,
  useEditor,
  useInstance
} from '@milkdown/react'
import type { CmdKey } from '@milkdown/kit/core'
import { getMarkdownHeadingAttrs } from '../../utils/markdownUtils'

// ── Types ────────────────────────────────────────────────────────────────────

interface MarkdownEditorProps {
  id?: string
  value: string
  onChange: (val: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
  stickyTop?: number
  onFocusChange?: (focused: boolean) => void
  onValidChange?: (valid: boolean) => void
  charLimit?: number
}

type GetInstance = () => Editor | undefined
type CurrentRef<T> = { current: T }

const incrementRenderTick = (count: number): number => count + 1

const getFocusableToolbarButtons = (
  toolbar: HTMLDivElement
): HTMLButtonElement[] => {
  return Array.from(
    toolbar.querySelectorAll<HTMLButtonElement>('button:not([disabled])')
  )
}

const moveToolbarFocus = (
  buttons: HTMLButtonElement[],
  current: HTMLButtonElement,
  direction: 'left' | 'right'
): boolean => {
  const currentIndex = buttons.indexOf(current)
  if (currentIndex === -1) return false

  const nextIndex =
    direction === 'right'
      ? (currentIndex + 1) % buttons.length
      : (currentIndex - 1 + buttons.length) % buttons.length

  buttons[nextIndex].focus()
  return true
}

// ── Toolbar helpers ──────────────────────────────────────────────────────────

function callCmd<T>(
  getInstance: GetInstance,
  cmd: { key: CmdKey<T> },
  payload?: T
) {
  const editor = getInstance()
  if (!editor) return
  editor.action(callCommand(cmd.key, payload))
}

function isNodeActive(
  getInstance: GetInstance,
  typeName: string,
  level?: number
): boolean {
  const editor = getInstance()
  if (!editor) return false
  try {
    return editor.action((ctx) => {
      const state = ctx.get(editorStateCtx)
      const { $from } = state.selection
      for (let d = $from.depth; d >= 0; d--) {
        const node = $from.node(d)
        if (node.type.name === typeName) {
          if (level !== undefined) return node.attrs.level === level
          return true
        }
      }
      return false
    })
  } catch {
    return false
  }
}

function isMarkActive(getInstance: GetInstance, markName: string): boolean {
  const editor = getInstance()
  if (!editor) return false
  try {
    return editor.action((ctx) => {
      const state = ctx.get(editorStateCtx)
      const { from, to, empty } = state.selection
      const schema = state.schema
      const markType = schema.marks[markName]
      if (!markType) return false
      if (empty) {
        return !!markType.isInSet(
          state.storedMarks ?? state.selection.$from.marks()
        )
      }
      let found = false
      state.doc.nodesBetween(from, to, (node) => {
        if (markType.isInSet(node.marks)) found = true
      })
      return found
    })
  } catch {
    return false
  }
}

// ── Toolbar definition ───────────────────────────────────────────────────────

type ActiveFn = (getInstance: GetInstance) => boolean

type ToolbarDivider = { type: 'divider'; key: string }
type ToolbarButton = {
  type: 'button'
  icon: React.ReactNode
  label: string
  action: (getInstance: GetInstance) => void
  isActive?: ActiveFn
  isLink?: boolean
}
type ToolbarItem = ToolbarDivider | ToolbarButton

const TOOLBAR: ToolbarItem[] = [
  {
    type: 'button',
    icon: <TextBIcon size={16} />,
    label: 'Fet',
    action: (g) => callCmd(g, toggleStrongCommand),
    isActive: (g) => isMarkActive(g, 'strong')
  },
  {
    type: 'button',
    icon: <TextItalicIcon size={16} />,
    label: 'Kursiv',
    action: (g) => callCmd(g, toggleEmphasisCommand),
    isActive: (g) => isMarkActive(g, 'emphasis')
  },
  { type: 'divider', key: 'text-divider' },
  {
    type: 'button',
    icon: <TextHOneIcon size={16} />,
    label: 'Overskrift 1',
    action: (g) =>
      isNodeActive(g, 'heading', 1)
        ? callCmd(g, turnIntoTextCommand)
        : callCmd(g, wrapInHeadingCommand, 1),
    isActive: (g) => isNodeActive(g, 'heading', 1)
  },
  {
    type: 'button',
    icon: <TextHTwoIcon size={16} />,
    label: 'Overskrift 2',
    action: (g) =>
      isNodeActive(g, 'heading', 2)
        ? callCmd(g, turnIntoTextCommand)
        : callCmd(g, wrapInHeadingCommand, 2),
    isActive: (g) => isNodeActive(g, 'heading', 2)
  },
  {
    type: 'button',
    icon: <TextHThreeIcon size={16} />,
    label: 'Overskrift 3',
    action: (g) =>
      isNodeActive(g, 'heading', 3)
        ? callCmd(g, turnIntoTextCommand)
        : callCmd(g, wrapInHeadingCommand, 3),
    isActive: (g) => isNodeActive(g, 'heading', 3)
  },
  { type: 'divider', key: 'heading-divider' },
  {
    type: 'button',
    icon: <ListBulletsIcon size={16} />,
    label: 'Punktliste',
    action: (g) => callCmd(g, wrapInBulletListCommand),
    isActive: (g) => isNodeActive(g, 'bullet_list')
  },
  {
    type: 'button',
    icon: <ListNumbersIcon size={16} />,
    label: 'Nummerert liste',
    action: (g) => callCmd(g, wrapInOrderedListCommand),
    isActive: (g) => isNodeActive(g, 'ordered_list')
  },
  { type: 'divider', key: 'list-divider' },
  {
    type: 'button',
    icon: <LinkSimpleIcon size={16} />,
    label: 'Lenke',
    isLink: true,
    action: () => {
      /* handled via onLinkClick in EditorToolbar */
    }
  }
]

// ── ToolbarButton component ──────────────────────────────────────────────────

interface ToolbarButtonComponentProps {
  item: Extract<ToolbarItem, { type: 'button' }>
  isReadonly: boolean
  getInstance: GetInstance
  onLinkClick?: () => void
}

function ToolbarButtonComponent({
  item,
  isReadonly,
  getInstance,
  onLinkClick
}: Readonly<ToolbarButtonComponentProps>) {
  const active = item.isActive?.(getInstance) ?? false
  return (
    <button
      type='button'
      title={item.label}
      aria-label={item.label}
      aria-pressed={item.isActive ? active : undefined}
      disabled={isReadonly}
      onMouseDown={(e) => {
        e.preventDefault()
        if (onLinkClick) {
          onLinkClick()
        } else {
          item.action(getInstance)
        }
      }}
      className='rounded px-1.5 py-1 transition-colors active:scale-95 disabled:opacity-40'
      style={{
        color: 'var(--mtds-color-text-default)',
        backgroundColor: active ? 'var(--mtds-color-surface-active)' : undefined
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor =
            'var(--mtds-color-surface-hover)'
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = active
          ? 'var(--mtds-color-surface-active)'
          : ''
      }}
    >
      {item.icon}
    </button>
  )
}

// ── EditorToolbar component ──────────────────────────────────────────────────
// Lives in its own component so its forceUpdate state does NOT re-render
// MilkdownProvider — which would destroy/recreate the ProseMirror editor.

interface EditorToolbarProps {
  getInstance: GetInstance
  isReadonly: boolean
  stickyTop?: number
  onLinkClick: () => void
}

function EditorToolbar({
  getInstance,
  isReadonly,
  stickyTop,
  onLinkClick
}: Readonly<EditorToolbarProps>) {
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [, forceUpdate] = useState(0) // NOSONAR
  const refreshToolbar = useCallback(() => {
    forceUpdate(incrementRenderTick)
  }, [])

  // Debounced via rAF so we don't thrash on rapid key/mouse events
  useEffect(() => {
    let raf: number
    const cb = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(refreshToolbar)
    }
    document.addEventListener('mouseup', cb)
    document.addEventListener('keyup', cb)
    return () => {
      document.removeEventListener('mouseup', cb)
      document.removeEventListener('keyup', cb)
      cancelAnimationFrame(raf)
    }
  }, [refreshToolbar])

  const borderColor = 'var(--mtds-color-border-default)'

  const handleToolbarKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const toolbar = toolbarRef.current
    if (!toolbar) return

    const buttons = getFocusableToolbarButtons(toolbar)
    const current = document.activeElement as HTMLButtonElement

    if (e.key === 'ArrowRight') {
      if (moveToolbarFocus(buttons, current, 'right')) {
        e.preventDefault()
      }
    }

    if (e.key === 'ArrowLeft') {
      if (moveToolbarFocus(buttons, current, 'left')) {
        e.preventDefault()
      }
    }
  }

  return (
    <div
      ref={toolbarRef}
      role='toolbar'
      aria-label='Tekstformatering'
      className='flex flex-wrap items-center gap-0.5 px-1 py-1'
      onKeyDown={handleToolbarKeyDown}
      style={{
        backgroundColor: 'transparent',
        border: `1px solid ${borderColor}`,
        borderRadius:
          'var(--mtds-border-radius-md) var(--mtds-border-radius-md) 0 0',
        transition: 'border-color 0.15s',
        position: 'sticky',
        top: stickyTop ?? 0,
        zIndex: 9
      }}
    >
      {TOOLBAR.map((item) =>
        item.type === 'divider' ? (
          <span
            key={item.key}
            className='mx-1 inline-block h-4 w-px self-center'
            style={{
              backgroundColor: 'var(--mtds-color-border-subtle)'
            }}
          />
        ) : (
          <ToolbarButtonComponent
            key={item.label}
            item={item}
            isReadonly={isReadonly}
            getInstance={getInstance}
            onLinkClick={item.isLink ? onLinkClick : undefined}
          />
        )
      )}
    </div>
  )
}

// ── Inner Milkdown editor (must live inside MilkdownProvider) ────────────────

interface MilkdownEditorProps {
  initialValue: string
  onChangeRef: CurrentRef<(val: string) => void>
  onFocusRef: CurrentRef<(() => void) | undefined>
  onBlurRef: CurrentRef<(() => void) | undefined>
  onInstanceReady: (getInstance: GetInstance) => void
  lastEmittedRef: CurrentRef<string>
}

function MilkdownEditor({
  initialValue,
  onChangeRef,
  onFocusRef,
  onBlurRef,
  onInstanceReady,
  lastEmittedRef
}: Readonly<MilkdownEditorProps>) {
  useEditor((root) => {
    return Editor.make()
      .config((ctx) => {
        ctx.set(headingAttr.key, (node) =>
          getMarkdownHeadingAttrs(node.attrs.level)
        )
        ctx.set(rootCtx, root)
        ctx.set(defaultValueCtx, initialValue)
        ctx.set(editorViewOptionsCtx, {
          attributes: { class: styles.prose }
        })
      })
      .config((ctx) => {
        ctx
          .get(listenerCtx)
          .markdownUpdated((_ctx, markdown) => {
            lastEmittedRef.current = markdown
            onChangeRef.current(markdown)
          })
          .focus(() => {
            onFocusRef.current?.()
          })
          .blur(() => {
            onBlurRef.current?.()
          })
      })
      .use(commonmark)
      .use(listener)
      .use(history)
      .use(clipboard)
  }, [])

  const [loading, getInstance] = useInstance()

  useEffect(() => {
    if (!loading) {
      onInstanceReady(getInstance as GetInstance)
    }
  }, [loading, getInstance, onInstanceReady])

  return <Milkdown />
}

// ── Public component ─────────────────────────────────────────────────────────

const CHAR_LIMIT = 2000

const getCounterValue = (markdown: string) => markdown.replace(/\n+$/, '')

const syncCounterInput = (
  input: HTMLTextAreaElement | null,
  markdown: string
) => {
  if (!input) return
  input.value = getCounterValue(markdown)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

export const MarkdownEditor = ({
  id,
  value,
  onChange,
  disabled = false,
  readonly = false,
  placeholder = 'Skriv en beskrivelse...',
  stickyTop,
  onFocusChange,
  onValidChange,
  charLimit = CHAR_LIMIT
}: MarkdownEditorProps) => {
  const [isFocused, setIsFocused] = useState(false)

  // Link dialog — imperative ref so we call .showModal()/.close() directly
  // (avoids relying on the design-system MutationObserver to convert open={true})
  const linkDialogRef = useRef<HTMLDialogElement>(null)
  const linkDialogId = useId()
  const [linkUrl, setLinkUrl] = useState('')

  // Stable refs so editor listeners don't go stale
  const onChangeRef = useRef(onChange)
  const onFocusRef = useRef<(() => void) | undefined>(undefined)
  const onBlurRef = useRef<(() => void) | undefined>(undefined)
  const counterInputRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    onChangeRef.current = (next) => {
      onChange(next)
      syncCounterInput(counterInputRef.current, next)
    }
  }, [onChange])

  // Stable dep array — these callbacks never need to change identity
  useEffect(() => {
    onFocusRef.current = () => {
      setIsFocused(true)
      onFocusChange?.(true)
    }
    onBlurRef.current = () => {
      setIsFocused(false)
      onFocusChange?.(false)
    }
  }, []) // intentionally empty — these refs only need to be set once

  // Saved ProseMirror selection — captured on link button click, restored before applying command
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const savedSelectionRef = useRef<any>(null)

  // getInstance is set once the editor is ready
  const getInstanceRef = useRef<GetInstance>(() => undefined)

  // Stable identity so MilkdownEditor's useEffect doesn't re-fire on re-renders
  const handleInstanceReady = useCallback((getInstance: GetInstance) => {
    getInstanceRef.current = getInstance
  }, [])

  // Stable wrapper so EditorToolbar always reads the latest instance
  const stableGetInstance = useCallback(() => getInstanceRef.current(), [])

  // Sync external value changes into the editor (e.g. form reset to '')
  const lastEmittedRef = useRef(value)
  useEffect(() => {
    if (value === lastEmittedRef.current) return
    const editor = getInstanceRef.current()
    if (!editor) return
    editor.action(replaceAll(value))
    syncCounterInput(counterInputRef.current, value)
  }, [value])

  const handleLinkConfirm = () => {
    if (linkUrl.trim()) {
      const editor = stableGetInstance()
      if (editor && savedSelectionRef.current) {
        editor.action((ctx) => {
          const view = ctx.get(editorViewCtx)
          view.focus()
          const tr = view.state.tr.setSelection(savedSelectionRef.current)
          view.dispatch(tr)
        })
      }
      callCmd(stableGetInstance, toggleLinkCommand, {
        href: linkUrl.trim(),
        title: ''
      })
    }
    linkDialogRef.current?.close()
  }

  const handleLinkCancel = () => {
    savedSelectionRef.current = null
    setLinkUrl('')
  }

  const isReadonly = disabled || readonly
  const borderColor = 'var(--mtds-color-border-default)'
  const focusStyles = isFocused
    ? {
        boxShadow: 'var(--mtdsc-focus-boxShadow)',
        outline: 'var(--mtdsc-focus-outline)',
        outlineOffset: 'var(--mtds-border-width-focus)'
      }
    : undefined
  const overLimit = getCounterValue(value).length > charLimit

  useEffect(() => {
    onValidChange?.(!overLimit)
  }, [overLimit, onValidChange])

  return (
    <div id={id}>
      <Mtds.Field>
        <div
          style={{
            borderRadius: 'var(--mtds-border-radius-md)',
            ...focusStyles
          }}
        >
          {/* Toolbar lives in its own component to isolate forceUpdate re-renders */}
          <EditorToolbar
            getInstance={stableGetInstance}
            isReadonly={isReadonly}
            stickyTop={stickyTop}
            onLinkClick={() => {
              const editor = stableGetInstance()
              if (editor) {
                try {
                  editor.action((ctx) => {
                    savedSelectionRef.current =
                      ctx.get(editorStateCtx).selection
                  })
                } catch {
                  // ignore — dialog still opens even if selection can't be saved
                }
              }
              linkDialogRef.current?.showModal()
            }}
          />

          {/* Editor area */}
          <div
            style={{
              border: `1px solid ${borderColor}`,
              borderTop: 'none',
              borderRadius:
                '0 0 var(--mtds-border-radius-md) var(--mtds-border-radius-md)',
              transition: 'border-color 0.15s',
              overflow: 'hidden'
            }}
          >
            <div
              className='relative'
              style={{
                fontFamily: 'var(--ds-font-family)',
                fontSize: 'var(--mtds-font-size-md)',
                lineHeight: 'var(--mtds-line-height-md)',
                color: 'var(--mtds-color-neutral-text-default)',
                backgroundColor: 'transparent',
                minHeight: '8rem'
              }}
            >
              <MilkdownProvider>
                <MilkdownEditor
                  initialValue={value}
                  onChangeRef={onChangeRef}
                  onFocusRef={onFocusRef}
                  onBlurRef={onBlurRef}
                  onInstanceReady={handleInstanceReady}
                  lastEmittedRef={lastEmittedRef}
                />
              </MilkdownProvider>
              {/* Placeholder: shown when editor is empty and not focused */}
              {!value.trim() && !isFocused && (
                <span
                  className='pointer-events-none absolute left-4 top-3 select-none'
                  style={{ color: 'var(--mtds-color-neutral-text-subtle)' }}
                  aria-hidden
                >
                  {placeholder}
                </span>
              )}
            </div>
          </div>
        </div>
      </Mtds.Field>
      {charLimit > 0 && (
        <Mtds.Field>
          <textarea
            ref={counterInputRef}
            aria-hidden='true'
            tabIndex={-1}
            defaultValue={getCounterValue(value)}
            className='sr-only'
          />
          <Mtds.Validation data-field='counter' data-limit={charLimit} />
        </Mtds.Field>
      )}

      {/* Link dialog */}
      <Mtds.Dialog
        ref={linkDialogRef}
        id={linkDialogId}
        onClose={handleLinkCancel}
        onClick={(e: React.MouseEvent<HTMLDialogElement>) => {
          // Backdrop click: target is the <dialog> element itself
          if (e.target === linkDialogRef.current) {
            linkDialogRef.current?.close()
          }
        }}
      >
        <Mtds.Button
          command='close'
          commandfor={linkDialogId}
          aria-label='Lukk'
        />
        <Mtds.Prose>
          <Mtds.Heading>Legg til lenke</Mtds.Heading>
          <Mtds.Field>
            <label htmlFor='link-dialog-url'>URL</label>
            <Mtds.Input
              id='link-dialog-url'
              type='url'
              placeholder='https://'
              value={linkUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLinkUrl(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') handleLinkConfirm()
              }}
            />
          </Mtds.Field>
          <Mtds.Flex style={{ paddingTop: 'var(--mtds-8)' }}>
            <Mtds.Button
              command='close'
              commandfor={linkDialogId}
              type='button'
            >
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='button'
              onClick={handleLinkConfirm}
            >
              Legg til lenke
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Prose>
      </Mtds.Dialog>
    </div>
  )
}
